<div class="landingPreference" *transloco="let translate">
    <header class="sectionHeader">
        <div class="sectionHeaderText">
            <div class="sectionTitle" [textContent]="translate('Landing Page')"></div>
            <p class="sectionSubtitle" [textContent]="translate('Choose the page you want to see first after signing in. Per-tenant settings take priority; the global setting applies as a fallback.')"></p>
        </div>
        <button *ngIf="hasSavedPreference" type="button" class="resetLink" (click)="resetToDefault()" [title]="translate('Remove your saved preference and go back to the usual behaviour')">
            <span class="material-symbols-outlined resetIcon" aria-hidden="true">restart_alt</span>
            <span [textContent]="translate('Reset to default')"></span>
        </button>
    </header>

    <!-- scrollable content area (excludes action bar) -->
    <div class="prefScrollBody">

    <!-- ===== SECTION 1: All Tenants ===== -->
    <section class="prefSection">
        <div class="prefSectionHeader">
            <span class="material-symbols-outlined prefSectionIcon" aria-hidden="true">public</span>
            <div class="prefSectionMeta">
                <h2 class="prefSectionTitle" [textContent]="translate('For all tenants')"></h2>
                <p class="prefSectionHint" [textContent]="translate('Global fallback — applied when a tenant has no specific page set below.')"></p>
            </div>
            <span *ngIf="allTenantsPage" class="activeBadge">
                <span class="material-symbols-outlined" aria-hidden="true">check_circle</span>
                <span [textContent]="translate('Active')"></span>
            </span>
        </div>
        <div class="pageChips" role="radiogroup" [attr.aria-label]="translate('Global landing page')">
            <button
                    *ngFor="let page of pageOptions; let i = index"
                    type="button"
                    class="pageChip"
                    role="radio"
                    [attr.aria-checked]="allTenantsPage === page.id"
                    [class.selected]="allTenantsPage === page.id"
                    (click)="selectAllTenantsPage(page.id)"
                    [style.animation-delay]="i * 35 + 'ms'">
                <span class="material-symbols-outlined pageChipIcon" aria-hidden="true">{{ page.icon }}</span>
                <span class="pageChipName" [textContent]="translate(page.name)"></span>
                <span *ngIf="page.id === defaultPageId" class="pageChipBadge" [textContent]="translate('Default')"></span>
                <span class="pageChipCheck" aria-hidden="true">
                    <span class="material-symbols-outlined">check</span>
                </span>
            </button>
        </div>
        <div class="chipDescriptionStrip">
            <ng-container *ngFor="let page of [getPageOption(allTenantsPage)]">
                <span class="chipDescription" *ngIf="page">
                    <span class="material-symbols-outlined chipDescriptionIcon" aria-hidden="true">{{ page.icon }}</span>
                    <span [textContent]="translate(page.description)"></span>
                </span>
                <span class="chipDescription chipDescriptionEmpty" *ngIf="!page">
                    <span class="material-symbols-outlined chipDescriptionIcon" aria-hidden="true">info</span>
                    <span [textContent]="translate('No global page selected — app default will be used as fallback.')"></span>
                </span>
            </ng-container>
        </div>
    </section>

    <div class="sectionDivider">
        <span class="sectionDividerLine"></span>
        <span class="sectionDividerLabel" [textContent]="translate('Per-tenant overrides')"></span>
        <span class="sectionDividerLine"></span>
    </div>

    <!-- ===== SECTION 2: Per Tenant ===== -->
    <section class="prefSection tableSection">
        <div class="prefSectionHeader">
            <span class="material-symbols-outlined prefSectionIcon" aria-hidden="true">account_tree</span>
            <div class="prefSectionMeta">
                <h2 class="prefSectionTitle" [textContent]="translate('For each tenant')"></h2>
                <p class="prefSectionHint" [textContent]="translate('Override the landing page for specific tenants. These take priority over the global setting.')"></p>
            </div>
            <span *ngIf="configuredTenantsCount > 0" class="activeBadge">
                <span class="material-symbols-outlined" aria-hidden="true">check_circle</span>
                <span>{{ configuredTenantsCount }}&nbsp;<ng-container [ngTemplateOutlet]="configuredLabel"></ng-container></span>
            </span>
        </div>

        <div class="tableToolbar">
            <div class="tenantSearchBox">
                <span class="material-symbols-outlined searchIcon" aria-hidden="true">search</span>
                <input class="tenantSearchInput" type="text" autocomplete="off" [placeholder]="translate('Search tenants')" [attr.aria-label]="translate('Search tenants')" [value]="tenantSearch" (input)="filterTenants($any($event.target).value)" />
                <button type="button" class="clearSearch" *ngIf="tenantSearch" (click)="filterTenants('')" [title]="translate('Clear search')">
                    <span class="material-symbols-outlined" aria-hidden="true">close</span>
                </button>
            </div>
            <button type="button" class="quickActionButton" [disabled]="configuredTenantsCount === 0" (click)="clearAllTenantPages()" [textContent]="translate('Clear all')"></button>
        </div>

        <div class="tenantTableWrap">
            <table class="tenantTable">
                <thead>
                    <tr>
                        <th class="tenantColHead" [textContent]="translate('Tenant')"></th>
                        <th class="pageColHead" [textContent]="translate('Landing page')"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let tenant of filteredTenants; let i = index; trackBy: trackTenant" class="tenantRow" [class.assigned]="tenantPages[tenant.id]" [style.animation-delay]="(i < 16 ? i * 22 : 0) + 'ms'">
                        <td class="tenantCell">
                            <div class="tenantCellInner">
                                <span class="material-symbols-outlined tenantIcon" aria-hidden="true">{{ tenant.depth === 0 ? 'corporate_fare' : 'subdirectory_arrow_right' }}</span>
                                <span class="tenantCellText">
                                    <span class="tenantPath" [title]="tenant.path">
                                        <ng-container *ngFor="let segment of tenant.pathSegments; let last = last; let first = first">
                                            <span class="pathSep" *ngIf="!first" aria-hidden="true">|</span>
                                            <span class="pathSeg" [class.leaf]="last" [textContent]="segment"></span>
                                        </ng-container>
                                        <span *ngIf="tenant.id === currentOrganizationId" class="tenantBadge" [textContent]="translate('Current')"></span>
                                    </span>
                                    <span class="tenantItemLogin" *ngIf="tenant.orgLoginId" [textContent]="tenant.orgLoginId"></span>
                                </span>
                            </div>
                        </td>
                        <td class="pageCell">
                            <div class="pageChips rowChips" role="radiogroup" [attr.aria-label]="translate('Landing page') + ' — ' + tenant.path">
                                <button *ngFor="let page of pageOptions" type="button" class="pageChip" role="radio" [attr.aria-checked]="tenantPages[tenant.id] === page.id" [class.selected]="tenantPages[tenant.id] === page.id" (click)="toggleTenantPage(tenant.id, page.id)">
                                    <span class="material-symbols-outlined pageChipIcon" aria-hidden="true">{{ page.icon }}</span>
                                    <span class="pageChipName" [textContent]="translate(page.name)"></span>
                                    <span *ngIf="page.id === defaultPageId" class="pageChipBadge" [textContent]="translate('Default')"></span>
                                    <span class="pageChipCheck" aria-hidden="true">
                                        <span class="material-symbols-outlined">check</span>
                                    </span>
                                </button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div *ngIf="tenants.length > 0 && filteredTenants.length === 0" class="emptyRows">
                <span class="material-symbols-outlined" aria-hidden="true">search_off</span>
                <span [textContent]="translate('No tenants match your search')"></span>
            </div>
            <div *ngIf="tenants.length === 0" class="emptyRows">
                <span class="material-symbols-outlined spin" aria-hidden="true">progress_activity</span>
                <span [textContent]="translate('Loading tenants')"></span>
            </div>
        </div>
    </section>

    </div><!-- /prefScrollBody -->

    <!-- Action bar -->
    <div class="actionBar" [class.dirty]="isDirty" role="status">
        <span class="actionBarInfo" *ngIf="!isDirty && !justSaved">
            <span class="material-symbols-outlined actionBarIcon" aria-hidden="true">schedule</span>
            <span [textContent]="translate('Changes take effect the next time you sign in.')"></span>
        </span>
        <span class="actionBarInfo pending" *ngIf="isDirty">
            <span class="pendingDot" aria-hidden="true"></span>
            <span [textContent]="translate('You have unsaved changes')"></span>
        </span>
        <span class="actionBarInfo saved" *ngIf="!isDirty && justSaved">
            <span class="material-symbols-outlined actionBarIcon savedIcon" aria-hidden="true">check_circle</span>
            <span [textContent]="translate('Your preference has been saved')"></span>
        </span>
        <span class="actionBarActions">
            <button type="button" class="discardButton" *ngIf="isDirty" (click)="discardChanges()" [textContent]="translate('Discard')"></button>
            <material-button [color]="'primary'" [text]="translate('Save Preferences')" [disabled]="!canSave()" (buttonClicked)="save()"></material-button>
        </span>
    </div>
</div>

<!-- template for "N configured" label -->
<ng-template #configuredLabel>{{ configuredTenantsCount === 1 ? 'tenant configured' : 'tenants configured' }}</ng-template>
